import React from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import InfoTooltipCell from 'components/Shared/MuiTable/Cell/InfoTooltipCell'
import { useNewTranslation } from 'hooks'
import { PLATFORM_ENUM, PLATFORM_LABEL } from 'services/api/constants'
import { GetCampaignOverviewResponse } from 'services/api/campaignReport'
import { CampaignState, FollowerState, REQUIREMENT_TYPE_ENUM, RequirementState } from 'state/campaigns'
import { convertToThousandSeparator } from 'components/utils'
export interface CampaignOverviewRowProps {
    id: string
    breakdown: {
        value: string
        info?: string
    }
    actual: {
        value: string | null
    }
    minimum: {
        value: string | null
    }
}

export const getCampaignOverviewColumnsDef = (): GridColDef[] => {
    const [t] = useNewTranslation()

    return [
        {
            field: 'breakdown',
            headerName: '',
            width: 240,
            resizable: false,
            cellClassName: 'title-cell',
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => <InfoTooltipCell {...params} />,
        },
        {
            field: 'actual',
            headerName: t('submission.campaignReport.actual.header'),
            minWidth: 120,
            align: 'center',
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            sortable: false,
            filterable: false,
            flex: 1,
        },
        {
            field: 'minimum',
            headerName: t('submission.campaignReport.minimum.header'),
            minWidth: 120,
            align: 'center',
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            flex: 1,
        },
    ]
}

const LABEL_MAPPING: { [key: number]: string } = {
    [PLATFORM_ENUM.IG]: PLATFORM_LABEL.INSTAGRAM,
    [PLATFORM_ENUM.FB]: PLATFORM_LABEL.FACEBOOK,
    [PLATFORM_ENUM.TIKTOK]: PLATFORM_LABEL.TIKTOK,
    [PLATFORM_ENUM.TWITTER]: PLATFORM_LABEL.TWITTER,
    [PLATFORM_ENUM.XIAOHONGSHU]: PLATFORM_LABEL.XIAOHONGSHU,
}

export const getCampaignOverviewRowsDef = (
    data: GetCampaignOverviewResponse,
    campaign: CampaignState,
): CampaignOverviewRowProps[] => {
    const [t] = useNewTranslation()

    const followerRequirements = campaign.requirements.find(
        (requirement: RequirementState) => requirement.type == REQUIREMENT_TYPE_ENUM.FOLLOWER,
    )

    const getMinimumFollowers = (slotNumber: number | null, platform?: PLATFORM_ENUM) => {
        if (!platform) return null
        const follower = followerRequirements?.properties.followers?.find(
            (follower: FollowerState) => follower.platform == platform,
        )
        if (!follower?.min || !slotNumber) return null
        return convertToThousandSeparator(follower.min * slotNumber)
    }

    const rows: CampaignOverviewRowProps[] = [
        {
            id: 'totalPartiposters',
            breakdown: {
                value: t('submission.campaignReport.totalPartiposters.title'),
                info: t('submission.campaignReport.totalPartiposters.tooltips'),
            },
            actual: { value: convertToThousandSeparator(data.total_completed) },
            minimum: { value: convertToThousandSeparator(campaign.slot_number) },
        },
        {
            id: 'totalInterestedPartiposters',
            breakdown: {
                value: t('submission.campaignReport.totalInterestedPartiposters.title'),
                info: t('submission.campaignReport.totalInterestedPartiposters.tooltips'),
            },
            actual: { value: convertToThousandSeparator(data.total_interested) },
            minimum: { value: null },
        },
    ]

    // Add follower count rows dynamically
    Object.entries(data.total_follower_count).forEach(([platformId, count], index) => {
        const platform = Number(platformId)

        if (platform && LABEL_MAPPING[platform]) {
            rows.push({
                id: `totalSocialMediaFollower_${platform}`,
                breakdown: {
                    value: t('submission.campaignReport.totalSocialMediaFollower.title', {
                        socialMediaPlatform: LABEL_MAPPING[platform],
                    }),
                },
                actual: { value: convertToThousandSeparator(count) },
                minimum: { value: getMinimumFollowers(campaign.slot_number, platform) },
            })
        }
    })

    // Add rejections/dropouts row
    rows.push({
        id: 'totalRejection',
        breakdown: { value: t('submission.campaignReport.totalRejection.title') },
        actual: { value: convertToThousandSeparator(data.total_dropout_reject) },
        minimum: { value: null },
    })

    return rows
}
